import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Hook for creator authentication via Supabase Auth.
 * Replaces the old localStorage-based creator_session.
 *
 * Supports:
 * - Supabase Auth session (new: JWT from tiktok-auth Edge Function)
 * - Admin debug mode (?creator=tiktok_id in URL)
 * - Test mode (legacy, for /my/login?test=true)
 *
 * Returns { tiktokId, loading, profile, isDebugMode }
 */
export function useCreatorAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiktokId, setTiktokId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [known, setKnown] = useState(true); // false = compte TikTok non rattaché à une agence

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // 1. Admin debug mode: ?creator=tiktok_id
        const debugCreatorId = searchParams.get('creator');
        if (debugCreatorId) {
          const { data: { user: adminUser } } = await supabase.auth.getUser();
          if (adminUser) {
            const { data: adminProfile } = await supabase
              .from('profiles')
              .select('role')
              .eq('id', adminUser.id)
              .single();
            if (adminProfile?.role === 'admin' || adminProfile?.role === 'tenant_admin' || adminProfile?.role === 'agency_owner') {
              setTiktokId(debugCreatorId);
              setIsDebugMode(true);
              setLoading(false);
              return;
            }
          }
        }

        // 2. Supabase Auth session (new flow)
        const { data: { user } } = await supabase.auth.getUser();

        if (user && user.user_metadata?.role === 'creator') {
          // Connu (rattaché à une agence) ou non : la session reste valide.
          // Inconnu → pas de tiktok_id agence ; on retombe sur l'open_id.
          const tid = user.user_metadata?.tiktok_id || user.user_metadata?.tiktok_open_id || null;
          setKnown(user.user_metadata?.known !== false);
          setTiktokId(tid);
          // Load cached profile for quick display
          try {
            const cached = localStorage.getItem('creator_profile');
            if (cached) setProfile(JSON.parse(cached));
          } catch { /* ignore */ }
          setLoading(false);
          return;
        }

        // 3. Legacy localStorage session (migration: redirect to re-auth)
        const legacy = localStorage.getItem('creator_session');
        if (legacy) {
          localStorage.removeItem('creator_session');
        }

        // Not authenticated
        navigate('/my/login', { replace: true });

      } catch {
        navigate('/my/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, searchParams]);

  const logout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('creator_profile');
    navigate('/my/login', { replace: true });
  };

  return { tiktokId, loading, profile, isDebugMode, known, logout };
}
